import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/apiClient";
import ProductCard from "../components/ProductCard";
import MegaMarquee from "../components/MegaMarquee";
import Countdown from "../components/Countdown";
import PressStrip from "../components/PressStrip";
import Lookbook from "../components/Lookbook";
import Voices from "../components/Voices";
import Seo from "../components/Seo";
import { ArrowRight, Target, Zap, Flame, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_SLIDES = [
  {
    src: "/img/choose-your-own-path-hero.webp",
    tag: "NYC · BROOKLYN BR · 選択",
    alt: "TOXIC STREET Choose Your Own Path tee — Brooklyn Bridge lifestyle",
  },
  {
    src: "/img/discipline-skate-hero.webp",
    tag: "LA · SKATE PARK · 規律",
    alt: "TOXIC STREET Discipline Over Everything tee — LA skate park sunset",
  },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Auto-advance hero slides every 6s
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(id);
  }, []);

  const featured = products.filter((p) => p.featured);
  const hero = featured[0] || products[0];
  // Arsenal shows everything; only exclude the hero when we have enough other products
  const remaining = products.length > 1 ? products.filter((p) => p.id !== hero?.id) : products;

  // Map product_id -> drop number based on created_at ASC (oldest = 001)
  const dropMap = useMemo(() => {
    const sorted = [...products].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const m = {};
    sorted.forEach((p, i) => { m[p.id] = i + 1; });
    return m;
  }, [products]);

  return (
    <div data-testid="home-page">
      <Seo
        title="DISCIPLINE OVER EVERYTHING · Rebel Streetwear"
        description="TOXIC STREET — rebel streetwear label. Limited-drop graphic tees, hoodies, and accessories for kids who refuse to fit in. Free US shipping over $100. Stay dangerous."
        canonical="https://toxicstreet.co/"
      />
      {/* === HERO — DROP 002 · SPLIT PANEL · BLACK === */}
      <section className="relative overflow-hidden border-b border-white/10 bg-ink lg:min-h-[100vh] lg:grid lg:grid-cols-2 lg:items-stretch">

        {/* LEFT — text + single CTA */}
        <div className="relative z-10 flex items-center px-5 sm:px-8 lg:pl-16 lg:pr-12 py-14 sm:py-24 lg:py-32">
          <div className="max-w-xl">
            <p className="text-xs sm:text-sm tracking-[0.4em] font-mono mb-6 flex items-center" style={{ color: "#f0abfc" }}>
              <span className="inline-block w-3 h-3 mr-2 animate-pulse" style={{ background: "linear-gradient(135deg, #dc143c, #a855f7)" }} />
              DROP 002 — LIVE NOW
            </p>

            <h1
              className="uppercase text-bone hero-headline"
              style={{ fontSize: "clamp(2.8rem, 8.2vw, 7.6rem)" }}
            >
              <span className="hero-line hero-line-1 font-ultra hero-tag">BORN</span>
              <span
                className="hero-line hero-line-2 font-ultra bg-clip-text text-transparent hero-tag"
                style={{
                  backgroundImage: "linear-gradient(90deg, #dc143c 0%, #f43f5e 30%, #c084fc 65%, #a855f7 100%)",
                  WebkitBackgroundClip: "text",
                }}
              >
                TO GO
              </span>
              <span className="hero-line hero-line-3 font-ultra text-bone/95 hero-tag hero-line-outlined">
                FURTHER<span style={{ color: "#f0abfc" }}>.</span>
              </span>
            </h1>

            <p className="mt-8 max-w-md text-bone/80 text-[15px] leading-[1.7] font-mono">
              Rebel streetwear for the kids who refuse to fit in. Limited drops. Heavyweight builds. Once they're gone, they're gone.
            </p>

            <div className="mt-8 sm:mt-10">
              <Link
                to="/shop"
                data-testid="hero-shop-drop-button"
                className="clay-btn clay-btn-primary group w-full sm:w-auto justify-center sm:justify-start"
              >
                <span>SHOP THE DROP</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-[10px] tracking-[0.3em] font-mono text-bone/70 uppercase border-t border-white/10 pt-6 max-w-md">
              <span className="flex items-center gap-2"><Flame size={12} style={{ color: "#f0abfc" }} /> 100 GLOBAL</span>
              <span className="flex items-center gap-2"><Target size={12} style={{ color: "#f0abfc" }} /> 240GSM</span>
              <span className="flex items-center gap-2"><Zap size={12} style={{ color: "#f0abfc" }} /> $52</span>
            </div>
          </div>
        </div>

        {/* RIGHT — full-height edge-to-edge lifestyle carousel (no card, autoplay + Ken Burns) */}
        <div className="relative lg:h-full h-[60vh] min-h-[400px] overflow-hidden">
          {HERO_SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.alt}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${i === slide ? "opacity-100 hero-kenburns" : "opacity-0"}`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchpriority={i === 0 ? "high" : "auto"}
            />
          ))}

          {/* Subtle left-edge fade so text remains readable on lg where columns meet */}
          <div className="hidden lg:block absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-ink to-transparent pointer-events-none" />

          {/* Top-left location tag (rotates with slide) */}
          <div className="absolute top-4 left-4 bg-black/50 backdrop-blur border border-white/20 px-3 py-1 text-[10px] tracking-[0.3em] font-mono text-bone">
            {HERO_SLIDES[slide].tag}
          </div>

          {/* Bottom-right live dot */}
          <div className="absolute bottom-6 right-6 flex items-center gap-2 bg-black/50 backdrop-blur border border-white/20 px-3 py-1 text-[10px] tracking-[0.3em] font-mono text-bone">
            <span style={{ color: "#f0abfc" }}>●</span> LIVE
          </div>

          {/* Prev / next */}
          <button
            type="button"
            aria-label="Previous slide"
            data-testid="hero-prev"
            onClick={() => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur border border-white/25 flex items-center justify-center text-bone hover:bg-black/70 transition"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            data-testid="hero-next"
            onClick={() => setSlide((s) => (s + 1) % HERO_SLIDES.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur border border-white/25 flex items-center justify-center text-bone hover:bg-black/70 transition"
          >
            <ChevronRight size={18} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => setSlide(i)}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-bone" : "w-3 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        </div>
      </section>

      <MegaMarquee />

      <Countdown />

      {/* === FEATURED PRODUCT SPOTLIGHT === */}
      {hero && (
        <section className="border-b border-white/20 bg-ink">
          <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 border-x border-white/15">
            <div className="relative aspect-square lg:aspect-auto bg-surface overflow-hidden group">
              <img src={hero.images?.[0]} alt={hero.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-4 left-4 bg-blood text-bone px-3 py-1 text-[10px] tracking-[0.3em] font-mono">FEATURED / 001</span>
              <span className="absolute bottom-4 left-4 font-marker text-bone text-3xl rotate-[-3deg]">heat.</span>
            </div>
            <div className="p-8 sm:p-12 lg:p-16 border-t lg:border-t-0 lg:border-l border-white/15 flex flex-col justify-center">
              <p className="text-blood text-xs tracking-[0.4em] font-mono">// SIGNATURE PIECE</p>
              <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter mt-3 leading-[0.9]">
                {hero.name}
              </h2>
              {hero.tagline && (
                <p className="font-marker text-blood text-2xl mt-4">{hero.tagline.toLowerCase()}</p>
              )}
              <p className="text-ash text-sm leading-relaxed mt-6 max-w-md">{hero.description}</p>
              <div className="mt-8 flex items-baseline gap-6">
                <span className="font-anton text-bone text-4xl">${hero.price.toFixed(2)}</span>
                <span className="text-[10px] tracking-[0.3em] font-mono text-ash">// FREE SHIP $100+</span>
              </div>
              <Link
                to={`/product/${hero.slug}`}
                data-testid="spotlight-shop-link"
                className="mt-10 inline-flex w-fit items-center gap-2 px-8 py-5 bg-blood text-bone text-xs tracking-[0.4em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors group"
              >
                CLAIM IT
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <PressStrip />

      {/* === THE FULL ARSENAL === */}
      <section className="border-b border-white/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-blood text-xs tracking-[0.3em] font-mono mb-2">// THE ARSENAL</p>
              <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter leading-[0.9]">
                NEW <span className="text-blood">WEAPONS</span>
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-link"
              className="text-bone text-xs tracking-[0.3em] uppercase font-mono hover:text-blood"
            >
              SEE EVERYTHING →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {remaining.slice(0, 6).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i + 1} drop={dropMap[p.id]} />
            ))}
          </div>
          {remaining.length === 0 && (
            <div data-testid="home-empty-state" className="border border-dashed border-white/20 py-20 px-6 text-center">
              <p className="text-blood text-xs tracking-[0.4em] font-mono mb-3">// VAULT LOCKED</p>
              <h3 className="font-anton uppercase text-bone text-4xl sm:text-6xl tracking-tighter">
                NEXT DROP <span className="text-blood">INCOMING</span>
              </h3>
              <p className="font-marker text-bone text-2xl mt-4">the arsenal is being restocked.</p>
              <p className="text-ash text-sm mt-2 font-mono uppercase tracking-[0.2em]">// stay close. stay dangerous.</p>
            </div>
          )}
        </div>
      </section>

      <Lookbook />

      {/* === MANIFESTO STRIP === */}
      <section className="border-b border-white/20 bg-ink relative overflow-hidden">
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 font-anton text-blood/10 text-[24rem] leading-none tracking-tighter select-none pointer-events-none rotate-[-8deg]">
          永
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-blood text-xs tracking-[0.4em] font-mono mb-3">// THE MANIFESTO</p>
            <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter leading-[0.9]">
              WE BUILT THIS<br/>FOR THE <span className="text-blood">RUTHLESS</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-bone">
            <p className="font-marker text-blood text-3xl">control your mind or it will control you.</p>
            <p className="text-base leading-relaxed">
              TOXIC STREET IS NOT FOR EVERYONE. IT'S FOR THE KIDS WHO WAKE UP AT 5AM,
              WHO RUN THE EXTRA MILE, WHO CHOSE THE GRIND OVER COMFORT. WE'RE NOT SELLING
              FABRIC. WE'RE SELLING A MINDSET.
            </p>
            <Link
              to="/manifesto"
              data-testid="manifesto-link"
              className="inline-block px-6 py-3 border border-bone text-bone text-xs tracking-[0.3em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors"
            >
              READ THE FULL CODE →
            </Link>
          </div>
        </div>
      </section>

      <Voices />

      <MegaMarquee words={["DROP 002 LOADING", "STAY DANGEROUS", "永遠の遺産"]} reverse />

      {/* === NEWSLETTER === */}
      <section className="border-b border-white/20 bg-blood relative">
        <div className="absolute inset-0 noise opacity-20" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24 text-center">
          <p className="text-bone text-xs tracking-[0.4em] font-mono mb-3 opacity-70">// JOIN THE STREET</p>
          <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter">
            EARLY ACCESS<br/>OR EAT DUST
          </h2>
          <p className="text-bone/80 text-sm mt-4 max-w-md mx-auto font-mono">
            DROPS SELL OUT IN MINUTES. NO ALERT, NO DRIP.
          </p>
          <form
            className="mt-8 max-w-md mx-auto flex border-2 border-bone"
            onSubmit={(e) => { e.preventDefault(); alert("WELCOME, REBEL."); }}
          >
            <input
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="YOU@DANGEROUS.COM"
              className="flex-1 bg-transparent px-4 py-4 text-bone text-sm font-mono uppercase tracking-[0.15em] focus:outline-none placeholder:text-bone/50"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              className="bg-bone text-blood px-6 py-4 text-xs tracking-[0.3em] uppercase font-mono hover:bg-ink hover:text-bone transition-colors"
            >
              JOIN →
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
